/**
 * Parse a fractional measurement string into a decimal number.
 * Accepts formats like:
 *   "96 5/16"  → 96.3125
 *   "96-5/16"  → 96.3125
 *   "5/16"     → 0.3125
 *   "96 1/2"   → 96.5
 *   "96.5"     → 96.5
 *   "96"       → 96
 *   ""         → NaN
 *
 * Returns NaN if the string cannot be parsed.
 */
export function parseFraction(input: string): number {
  const s = input.trim();
  if (!s) return NaN;

  // If it's a plain number (no slash), just parse it
  if (!s.includes('/')) {
    return parseFloat(s);
  }

  // Match: optional whole number, then fraction
  // Patterns: "96 5/16", "96-5/16", "5/16"
  const match = s.match(/^(\d+(?:\.\d+)?)?[\s-]*(\d+)\s*\/\s*(\d+)$/);
  if (!match) return NaN;

  const whole = match[1] ? parseFloat(match[1]) : 0;
  const numerator = parseInt(match[2], 10);
  const denominator = parseInt(match[3], 10);

  if (denominator === 0) return NaN;

  return whole + numerator / denominator;
}

/**
 * Convert a decimal number to a fraction string for display.
 * Uses 1/16" precision (standard tape measure).
 *   96.3125 → "96 5/16"
 *   96.5    → "96 1/2"
 *   96      → "96"
 *   0.3125  → "5/16"
 */
export function toFractionStr(decimal: number): string {
  if (isNaN(decimal)) return '';
  const whole = Math.floor(decimal);
  const remainder = decimal - whole;
  const sixteenths = Math.round(remainder * 16);

  if (sixteenths === 0) return `${whole}`;
  if (sixteenths === 16) return `${whole + 1}`;

  // Reduce fraction
  let num = sixteenths;
  let denom = 16;
  while (num % 2 === 0 && denom > 1) {
    num /= 2;
    denom /= 2;
  }

  if (whole === 0) return `${num}/${denom}`;
  return `${whole} ${num}/${denom}`;
}

/**
 * Format a value for display based on mode.
 * In fraction mode, shows "96 5/16". In decimal mode, shows "96.3125".
 */
export function formatDimension(value: string, fractionMode: boolean): string {
  if (!value || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  if (!fractionMode) return value;
  return toFractionStr(num);
}
